import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  signalStoreFeature,
  type,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import {
  addEntities,
  addEntity,
  removeAllEntities,
  removeEntity,
  setAllEntities,
  updateEntity,
  withEntities,
} from '@ngrx/signals/entities';
import { catchError, firstValueFrom, forkJoin, Observable, of, tap } from 'rxjs';

import { setPending, setFulfilled, withRequestStatus, withSyncToWebStorage } from '@shared/stores/store-features';
import { Activity } from '@shared/models/activity';
import { AbookDialogService } from '@shared/components/abook-dialog/abook-dialog.service';

import { ActivityService } from '@core/services/activity.service';
import { KMDSettingService } from '@core/services/kmdSetting.service';

import { SearchResponseTimeslot } from '@features/associations/models/search-response';
import { TimeslotCheckAvailabilityResponse } from '@features/associations/models/timeslot-check-availability-reponse';

import { BasketItem } from './basket';
import { BasketService } from './basket.service';
import { blockingMaxBookingTimeMessage } from './max-booking-time';
import { CombinedBasketItem } from './basket';
import { JourneyStore } from '@app/features/checkout/journey.store';
import { BasketCombinerService } from './services';

/**
 * Type guard to check if a basket item is a combined item
 */
function isCombinedBasketItem(item: BasketItem): item is CombinedBasketItem {
  return 'combinedTimeslotIds' in item && Array.isArray(item.combinedTimeslotIds);
}

/**
 * Guard: Check if adding a timeslot would create an availability type conflict
 * @param currentType - The current availability type in the basket (or null if empty)
 * @param incomingType - The availability type of the timeslot being added
 * @returns true if there's a conflict, false if OK to proceed
 */
function checkAvailabilityTypeConflict(currentType: string | null, incomingType: string): boolean {
  return currentType !== null && currentType !== incomingType;
}

/**
 * Keep an eye on this repo for extensions
 * @ngrx-traits/signals
 */

export interface SearchExpandedState {
  isKmd: boolean; // should belong to global Settings Store
  location: { id: number; name: string } | null; // location shared by all basket items
}

export const BasketStore = signalStore(
  // 👇 Providing `BasketStore` at the root level.
  { providedIn: 'root' },
  withEntities({ entity: type<BasketItem>(), collection: 'basketItem' }),
  withEntities({ entity: type<Activity>(), collection: 'activity' }),
  withRequestStatus(),
  signalStoreFeature(
    withState<SearchExpandedState>({
      isKmd: false,
      location: null, // location shared by all basket items
    }),
  ),
  withSyncToWebStorage({
    key: 'abook-basket-store',
    type: 'session',
    restoreOnInit: true,
    saveStateChangesAfterMs: 300,
  }),

  withComputed(({ basketItemIds, basketItemEntityMap, activityEntities }) => {
    const basketCombiner = inject(BasketCombinerService);
    const combinedBasketItems = computed(() => {
      const basketItems = basketItemIds().map((id) => basketItemEntityMap()[id]);
      return basketCombiner.combine(basketItems);
    });

    return {
      activities: computed(() => {
        return activityEntities().map((activity) => activity.name) as string[];
      }),
      // Count combined items (excludes overlapping periods)
      itemsCount: computed(() => combinedBasketItems().length),

      // Calculate total price from combined items (excludes overlapping periods)
      total: computed(() => {
        return combinedBasketItems().reduce((sum, item) => sum + item.price, 0);
      }),
      currentAvailabilityType: computed(() => {
        const [firstId] = basketItemIds();
        return basketItemEntityMap()[firstId]?.availabilityType ?? null;
      }),
      combinedBasketItems,
    };
  }),

  withMethods((store) => {
    const basketService = inject(BasketService);
    const activityService = inject(ActivityService);
    const kmdSettingService = inject(KMDSettingService);
    const dialogService = inject(AbookDialogService);
    const journeyStore = inject(JourneyStore);

    /**
     * Helper method to update all individual timeslots in a combined group
     * with validation results from a combined availability check.
     * Uses immutable updates via patchState and updateEntity.
     */
    const updateCombinedTimeslotsAvailability = (
      combinedTimeslotIds: string[],
      validationResult: TimeslotCheckAvailabilityResponse,
      count: number,
    ) => {
      const pricePerSlot = validationResult.price / count;

      // Ledighedstjekkets egen besked er grunden til at tiden ikke kan bookes; er der ingen, kan
      // maksimumtiden være det. Den vises på linjen, så det er tydeligt hvilken tid der er for meget.
      const message = validationResult.message || blockingMaxBookingTimeMessage(validationResult);

      combinedTimeslotIds.forEach((id) => {
        patchState(
          store,
          updateEntity(
            {
              id,
              changes: {
                isAvailable: validationResult.isAvailable,
                message,
                price: pricePerSlot,
              },
            },
            { collection: 'basketItem' },
          ),
        );
      });
    };

    /**
     * Helper method to validate a combined period and update all its timeslots
     * Returns an observable that completes after validation
     */
    const validateCombinedPeriod = (
      combinedItem: CombinedBasketItem,
      associationUserId?: number,
      bookingId?: number,
    ): Observable<TimeslotCheckAvailabilityResponse | null> => {
      return basketService
        .checkAvailability(
          combinedItem.resourceId,
          combinedItem.start,
          combinedItem.end,
          associationUserId,
          combinedItem.activityId,
          bookingId,
        )
        .pipe(
          tap((validationRes) => {
            updateCombinedTimeslotsAvailability(combinedItem.combinedTimeslotIds, validationRes, combinedItem.count);
          }),
          catchError((error) => {
            console.error('Error validating combined period:', error);
            return of(null);
          }),
        );
    };

    /**
     * Resolve availability type conflict by asking user to confirm clearing basket
     * @returns Promise<boolean> - true if user confirmed and basket cleared, false if cancelled
     */
    const resolveAvailabilityTypeConflict = async (): Promise<boolean> => {
      const confirmed = await dialogService.confirm({
        title:
          'Det er ikke muligt at foretage en booking og en forespørgsel samtidig. Vil du tømme kurven og fortsætte med den valgte tid?',
        message: '',
        confirmText: 'Ja tak',
        cancelText: 'Annuller',
      });

      if (confirmed) {
        patchState(store, removeAllEntities({ collection: 'basketItem' }), setFulfilled());
      }

      return confirmed;
    };

    return {
      /**
       * Resolve availability type conflict by asking user to confirm clearing basket
       * @returns Promise<boolean> - true if user confirmed and basket cleared, false if cancelled
       */
      resolveAvailabilityTypeConflict,

      /** Puts removed times back as they were — "Fortryd" after a removal (design 3a). */
      restoreBasketItems: (items: BasketItem[]) => {
        patchState(store, addEntities(items, { collection: 'basketItem' }), setFulfilled());
      },

      clearBasket: () => {
        patchState(store, removeAllEntities({ collection: 'basketItem' }), setFulfilled());
      },

      /**
       * Add timeslot to basket
       * Async method that can be awaited for loading state management
       */
      async addTimeslot({
        timeslot,
        associationUserId,
        resourceData,
        locationData,
        bookingId,
      }: {
        timeslot: SearchResponseTimeslot;
        associationUserId?: number;
        resourceData: { name: string | null; parentName?: string | null };
        locationData: { id: number; name: string };
        bookingId?: number;
      }): Promise<void> {
        try {
          // Guard: Check for availability type conflict
          const hasConflict = checkAvailabilityTypeConflict(store.currentAvailabilityType(), timeslot.availabilityType);

          if (hasConflict) {
            const confirmed = await resolveAvailabilityTypeConflict();
            if (!confirmed) {
              return; // User cancelled
            }
          }

          // Set loading state and update location
          patchState(store, setPending(), {
            location: locationData,
          });
          journeyStore.setJourneyAvailabilityType(timeslot.availabilityType);

          // Main operation: Check availability and add to basket
          const res = await firstValueFrom(
            basketService.checkAvailability(
              timeslot.resourceId,
              timeslot.start,
              timeslot.end,
              associationUserId,
              timeslot.activityId,
              bookingId,
            ),
          );

          patchState(
            store,
            addEntity(
              {
                id: timeslot.id,
                start: timeslot.start,
                end: timeslot.end,
                activityId: timeslot.activityId,
                resourceId: timeslot.resourceId,
                resourceName: resourceData.name,
                resourceParentName: resourceData.parentName,
                isAvailable: res.isAvailable,
                price: res.price,
                message: res.message,
                availabilityType: timeslot.availabilityType,
                minBookingMinutes: timeslot.minBookingMinutes,
                bookingTypeId: timeslot.bookingTypeId,
              } as BasketItem,
              { collection: 'basketItem' },
            ),
          );

          // If this timeslot was combined with others, validate the combined period
          const combinedItems = store.combinedBasketItems();
          const affectedCombinedItem = combinedItems.find((item) => item.combinedTimeslotIds.includes(timeslot.id));

          if (affectedCombinedItem && affectedCombinedItem.count > 1) {
            firstValueFrom(
              validateCombinedPeriod(affectedCombinedItem, associationUserId, bookingId).pipe(
                catchError(() => of(null)),
              ),
            ).catch(() => {
              /* ignore validation errors */
            });
          }
        } catch (error) {
          console.error('Error checking timeslot availability:', error);
          throw error;
        } finally {
          // Always reset loading state
          patchState(store, setFulfilled());
        }
      },

      /**
       * Remove timeslot from basket
       */
      removeTimeslot: (timeslot: SearchResponseTimeslot) => {
        const combinedItems = store.combinedBasketItems();
        const affectedCombinedItem = combinedItems.find((item) => item.combinedTimeslotIds.includes(timeslot.id));
        const wasInCombinedGroup = affectedCombinedItem && affectedCombinedItem.count > 1;

        patchState(store, removeEntity(timeslot.id, { collection: 'basketItem' }), setFulfilled());

        // If it was part of a combined group, validate remaining period(s)
        if (wasInCombinedGroup) {
          const updatedCombinedItems = store.combinedBasketItems();
          const remainingGroups = updatedCombinedItems.filter(
            (item) =>
              item.resourceId === affectedCombinedItem.resourceId &&
              item.combinedTimeslotIds.some((id) => affectedCombinedItem.combinedTimeslotIds.includes(id)),
          );

          // Validate remaining groups (fire and forget with proper error handling)
          remainingGroups.forEach((group) => {
            validateCombinedPeriod(group)
              .pipe(catchError(() => of(null)))
              .subscribe();
          });
        }
      },

      /**
       * Add pre-validated timeslot directly to basket without API call
       * Use this when the availability and price have already been checked
       */
      addPrevalidatedTimeslot: (
        timeslot: SearchResponseTimeslot,
        resourceData?: { name: string; parentName?: string },
      ) => {
        patchState(
          store,
          addEntity(
            {
              id: timeslot.id,
              start: timeslot.start,
              end: timeslot.end,
              activityId: timeslot.activityId,
              resourceId: timeslot.resourceId,
              resourceName: resourceData.name,
              resourceParentName: resourceData.parentName,
              isAvailable: timeslot.isAvailable,
              price: timeslot.price,
              message: null,
              availabilityType: timeslot.availabilityType,
              minBookingMinutes: timeslot.minBookingMinutes,
              bookingTypeId: timeslot.bookingTypeId,
            } as BasketItem,
            { collection: 'basketItem' },
          ),
        );
      },

      /**
       * Remove basket item (removes all timeslots in a combined group)
       */
      removeBasketItem: (basketItem: CombinedBasketItem) => {
        if (isCombinedBasketItem(basketItem)) {
          // Remove all timeslots in the combined group, no validation needed
          basketItem.combinedTimeslotIds.forEach((id) => {
            patchState(store, removeEntity(id, { collection: 'basketItem' }));
          });
          patchState(store, setFulfilled());
        }
      },

      getActivity: (activityId: number) => {
        return store.activityEntities().find((obj) => obj.id === activityId);
      },

      checkAvailability(
        basketItems: BasketItem[],
        associationUserId?: number,
        bookingId?: number,
      ): Observable<(TimeslotCheckAvailabilityResponse | null)[]> {
        const requests = basketItems.map((basketItem: BasketItem) => {
          // Check the full combined period
          return basketService
            .checkAvailability(
              basketItem.resourceId,
              basketItem.start,
              basketItem.end,
              associationUserId,
              basketItem.activityId,
              bookingId,
            )
            .pipe(
              tap((res) => {
                patchState(
                  store,
                  updateEntity(
                    {
                      id: basketItem.id,
                      changes: {
                        message: res.message,
                        isAvailable: res.isAvailable,
                        price: res.price,
                      },
                    },
                    { collection: 'basketItem' },
                  ),
                );

                // Update combined timeslots if this is a combined item
                if (isCombinedBasketItem(basketItem)) {
                  updateCombinedTimeslotsAvailability(basketItem.combinedTimeslotIds, res, basketItem.count);
                }
              }),
              catchError((error) => {
                console.error('check availability error:', error);

                return of(null);
              }),
            );
        });

        return forkJoin(requests); // Execute all HTTP requests in parallel
      },

      async load() {
        const kmdSettings = await firstValueFrom(kmdSettingService.get());
        patchState(store, { isKmd: kmdSettings.isKmd });

        const activities = (await firstValueFrom(activityService.getList())) as Activity[];
        patchState(store, setAllEntities(activities, { collection: 'activity' }));
      },
    };
  }),

  withHooks({ onInit: (store) => store.load() }),
);
